//#region src/kernel/kernel-origin.ts
/**
* The one form rule of a kernel origin (kernel apps, R-013, R-117).
*
* The chart writes `window.__ZION_KERNEL_ORIGIN__` on a DP page while the kernel origin is on
* (R-013). Three readers judge that value and the origins in the kernel 403 bodies: the shell
* loader on `<dp>` (WS-16a), the React SDK (`@zion/sdk-react`, WS-17) and the Node SDK (WS-23).
* R-117 gives them this one declaration, so the three cannot drift.
*
* Browser-safe: no Node builtin and no Node global. It reads the WHATWG `URL` class only.
*/
/** The first DNS label of every kernel origin host: `kernel.<dp>`. */
const KERNEL_HOST_LABEL = "kernel";
/** The fewest DNS labels a kernel origin host holds: `kernel`, the DP name, and a parent. */
const KERNEL_HOST_MIN_LABELS = 3;
/**
* True when `value` has the form of a kernel origin.
*
* Callers: the shell's `readKernelOrigin` on the configured global, the React SDK's
* `dataPlaneKernelOrigin`, `kernelRefusalOf` and `kernelSessionClaimsOf`, and the Node SDK.
*
* The value passes when all five rules hold:
*
* 1. `new URL(value)` parses it;
* 2. the protocol is `https:`;
* 3. the user name and the password are empty;
* 4. the serialised origin equals `value` exactly, so a path, a query, a fragment, a trailing
*    slash, an upper-case host and a default port written out each fail;
* 5. the host holds three labels or more, and its first label is `kernel`.
*
* So `https://kernel.a.b` and `https://kernel.a.b:8443` answer `true`. `https://kernel.a` (two
* labels), `https://kernel.a.b/x`, `https://app.kernel.a.b`, `https://kernelx.a.b`,
* `http://kernel.a.b` and any text that `URL` refuses answer `false`.
*
* This is a form check only. It does not bind `value` to one data plane: it answers `true` for
* `https://kernel.attacker.example`. A caller that opens an origin from an untrusted source must
* compare it with the origin of its own data plane. Pure; never throws.
*/
function isKernelOrigin(value) {
	let url;
	try {
		url = new URL(value);
	} catch {
		return false;
	}
	if (url.protocol !== "https:" || url.username !== "" || url.password !== "" || url.origin !== value) return false;
	const labels = url.hostname.split(".");
	return labels.length >= KERNEL_HOST_MIN_LABELS && labels[0] === KERNEL_HOST_LABEL;
}
//#endregion
export { isKernelOrigin };

//# sourceMappingURL=kernel-origin.js.map